import { Link, createFileRoute } from '@tanstack/react-router';
import { StatusChip } from '@/components/migration/file-drop';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { MODE_LABEL, STATUS_LABEL, STATUS_TONE, useImportRuns } from '@/lib/qbo-history';

export const Route = createFileRoute('/settings/migration/history/')({ component: History });

/** Screen G13 (document 06): every QuickBooks transaction import of the client. */
function History() {
  const orgId = useOrgId();
  const runs = useImportRuns();
  return (
    <>
      <PageHeader
        title="QuickBooks import history"
        description="Every check and import of QuickBooks transactions for this client, with what each brought over."
        actions={
          <Button asChild>
            <Link to="/settings/migration/transactions">Import transactions</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : runs.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : runs.isError ? (
        <EmptyState title="Could not load the imports" hint={runs.error.message} />
      ) : !runs.data.length ? (
        <EmptyState
          title="No QuickBooks imports yet"
          hint="Import transactions from Move from QuickBooks once the client is connected."
        />
      ) : (
        <Table className="max-w-6xl" data-testid="qbo-import-history">
          <TableHeader>
            <TableRow>
              <TableHead>Range</TableHead>
              <TableHead>Import</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Imported</TableHead>
              <TableHead className="text-right">Skipped</TableHead>
              <TableHead className="text-right">Not imported</TableHead>
              <TableHead>By</TableHead>
              <TableHead>Finished</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.data.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="tnum">
                  {formatDate(r.from)} – {formatDate(r.to)}
                </TableCell>
                <TableCell>{MODE_LABEL[r.mode]}</TableCell>
                <TableCell>
                  <StatusChip tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</StatusChip>
                </TableCell>
                <TableCell className="tnum text-right">{r.imported}</TableCell>
                <TableCell className="tnum text-right">{r.skipped}</TableCell>
                <TableCell className="tnum text-right">{r.failed}</TableCell>
                <TableCell>{r.startedByName ?? ''}</TableCell>
                <TableCell className="tnum">
                  {r.finishedAt ? formatDateTime(r.finishedAt) : ''}
                </TableCell>
                <TableCell className="text-right">
                  <Link
                    className="text-sm underline"
                    to="/settings/migration/transactions"
                    search={{ run: r.id }}
                  >
                    Open
                  </Link>
                  {r.imported || r.skipped ? (
                    <Link
                      className="ml-3 text-sm underline"
                      to="/settings/migration/history/$runId"
                      params={{ runId: r.id }}
                    >
                      Transactions
                    </Link>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
